import { useLang } from "@/context/LanguageContext";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Pricing({ onOpenModal }) {
  const { lang, t } = useLang();

  const handleSelect = () => {
    if (onOpenModal) onOpenModal();
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-32 bg-[#F0FDF4]/40"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Heading */}
        <div className="max-w-2xl mx-auto text-center">
          <span
            data-testid="pricing-eyebrow"
            className="font-body text-sm uppercase tracking-widest font-bold text-[#22C55E]"
          >
            {t.pricing.eyebrow}
          </span>
          <h2
            data-testid="pricing-title"
            className="mt-4 font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-[#064E3B] leading-[1.05]"
          >
            {t.pricing.title}
          </h2>
          <p
            data-testid="pricing-subtitle"
            className="mt-5 font-body text-lg text-[#047857]"
          >
            {t.pricing.subtitle}
          </p>
        </div>

        {/* Pricing grid */}
        <div className="mt-16 grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {t.pricing.tiers.map((tier) => {
            const isPro = tier.id === "pro";
            return (
              <div
                key={tier.id}
                data-testid={`pricing-card-${tier.id}`}
                className={`relative flex flex-col bg-white rounded-[2.5rem] p-8 md:p-10 transition-all duration-500 hover:-translate-y-2 ${
                  isPro
                    ? "border-2 border-[#22C55E] shadow-2xl shadow-green-900/15 lg:scale-105 lg:-mt-2"
                    : "border border-[#D1FAE5] shadow-sm hover:shadow-xl"
                }`}
              >
                {isPro && (
                  <div
                    data-testid="pricing-pro-badge"
                    className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-[#F59E0B] text-[#064E3B] px-4 py-1.5 rounded-full font-body text-xs font-black uppercase tracking-wider shadow-lg"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {t.pricing.most_popular}
                  </div>
                )}

                <div>
                  <h3
                    data-testid={`pricing-name-${tier.id}`}
                    className="font-heading text-3xl font-black text-[#064E3B]"
                  >
                    {tier.name}
                  </h3>
                  <p className="mt-1 font-body text-sm text-[#047857]/80">
                    {tier.tagline}
                  </p>

                  {/* Setup price */}
                  <div className="mt-6">
                    <div className="font-body text-[11px] uppercase tracking-widest font-bold text-[#047857]/50 mb-1">
                      {t.pricing.setup_label}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span
                        data-testid={`pricing-setup-${tier.id}`}
                        className="font-heading text-5xl md:text-6xl font-black text-[#064E3B]"
                      >
                        {tier.setupPrice.toLocaleString("cs")}
                      </span>
                      <span className="font-heading text-2xl font-bold text-[#064E3B]">
                        {t.pricing.currency}
                      </span>
                    </div>
                  </div>

                  {/* Monthly fee */}
                  <div className="mt-3 flex items-baseline gap-1.5 border-t border-[#D1FAE5] pt-3">
                    <span className="font-body text-sm text-[#047857]/70">
                      {t.pricing.monthly_label}
                    </span>
                    <span
                      data-testid={`pricing-monthly-${tier.id}`}
                      className="font-heading text-xl font-black text-[#064E3B]"
                    >
                      {tier.monthlyFee.toLocaleString("cs")}
                    </span>
                    <span className="font-heading text-base font-bold text-[#064E3B]">
                      {t.pricing.currency}
                    </span>
                    <span className="font-body text-sm text-[#047857]/70">
                      {t.pricing.period}
                    </span>
                  </div>
                </div>

                <ul className="mt-8 space-y-3.5 flex-1">
                  {tier.features.map((f, i) => (
                    <li
                      key={i}
                      data-testid={`pricing-feature-${tier.id}-${i}`}
                      className="flex items-start gap-3"
                    >
                      <span
                        className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                          isPro ? "bg-[#22C55E]" : "bg-[#D1FAE5]"
                        }`}
                      >
                        <Check
                          className={`w-3 h-3 ${
                            isPro ? "text-white" : "text-[#22C55E]"
                          }`}
                          strokeWidth={3}
                        />
                      </span>
                      <span className="font-body text-[15px] text-[#064E3B] leading-snug">
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`pricing-cta-${tier.id}`}
                  onClick={() => handleSelect(tier.id)}
                  className={`mt-8 inline-flex items-center justify-center h-13 py-3.5 rounded-full font-body font-bold text-base transition-all duration-300 hover:-translate-y-0.5 ${
                    isPro
                      ? "bg-[#22C55E] text-white shadow-xl shadow-green-900/20 hover:shadow-2xl hover:bg-[#16A34A]"
                      : tier.id === "premium"
                        ? "bg-[#064E3B] text-white hover:bg-[#22C55E]"
                        : "bg-[#FDFBF7] text-[#064E3B] border border-[#D1FAE5] hover:bg-[#D1FAE5]"
                  }`}
                >
                  {tier.id === "premium" ? t.pricing.cta_premium : t.pricing.cta}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
